import EmailLink from "@/components/atoms/EmailLink";
import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import Loader from "@/components/atoms/Loader";
import Modal from "@/components/atoms/Modal";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import SoftBadge from "@/components/atoms/SoftBadge";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import { PaginateCardProps } from "@/interfaces/Card";
import { IFilters } from "@/interfaces/Fetch";
import {
  faAt,
  faClock,
  faEllipsisVertical,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { ElementRef, FC, useEffect, useRef, useState } from "react";
import { Col, Dropdown, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import AreYouSurToContinueModal from "../../modals/AreYouSurToContinueModal";
import PaginateCard from "../PaginateCard";
import { toast, ToastContentProps } from "react-toastify";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  useDeleteCompanyCoursePersonMutation,
  useGetCompanyCoursePeopleQuery,
} from "@/services/companies/courses/people";

const CompanyCoursePeopleCard: FC<
  PaginateCardProps & IFilters & { id: string; courseId: string }
> = ({ onGetTotal, onDelete: onDeleteProp, id, courseId, ...props }) => {
  const DeleteModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [filters, setFilters] = useState<IFilters>({
    pageIndex: 0,
    pageSize: 10,
    ...props,
  });
  const [deleteCompanyCoursePersonAsync] = useDeleteCompanyCoursePersonMutation();

  const { data, isLoading, isError, error } = useGetCompanyCoursePeopleQuery([
    id,
    courseId,
    filters,
  ]);

  useEffect(() => {
    data?.meta && onGetTotal?.(data.meta.total);
  }, [data?.meta]);

  const onDeleteClick = (id: string) => {
    onDeleteProp
      ? onDeleteProp(id)
      : DeleteModalRef.current?.showOrHiddenModal(id);
  };

  const onDelete = (personId: string) => {
    toast.promise(
      deleteCompanyCoursePersonAsync([id, courseId, personId]).unwrap(),
      {
        pending: "Retrait de l'apprenant de la formation en cours...",
        success: {
          render() {
            DeleteModalRef.current?.showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                L'apprenant a été retiré de la formation.
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  if (isLoading) {
    return <Loader />;
  } else if (isError) {
    return <ErrorComponent error={error} />;
  }

  return (
    <PaginateCard meta={data?.meta} filters={filters} setFilters={setFilters}>
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment retirer cet apprenant de la formation ?"
      />
      {data?.data.map(({ id, fullname, phone, email, createdAt, isOwner }) => (
        <div
          key={id}
          className="bg-white dark__bg-1100 p-x1 rounded-3 shadow-sm"
        >
          <Row className="flex-between-center">
            <Col xs={10}>
              <Link
                to={`/people/${id}`}
                className="d-flex align-items-center fw-semi-bold"
              >
                {fullname}
              </Link>
            </Col>
            <Col xs="auto" className="d-flex justify-content-end">
              <Dropdown
                align="end"
                className="btn-reveal-trigger d-inline-block"
              >
                <Dropdown.Toggle split variant="nolina-default" size="sm">
                  <FontAwesomeIcon
                    icon={faEllipsisVertical}
                    className="fs--2"
                  />
                </Dropdown.Toggle>

                <Dropdown.Menu className="border py-0">
                  <div className="py-2">
                    <Dropdown.Item
                      className="text-danger"
                      onClick={() => onDeleteClick(id.toString())}
                    >
                      Supprimer
                    </Dropdown.Item>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
          <div className="border-bottom border-lg-0 border-0 my-3"></div>
          <Row className="flex-between-center">
            <Col xs={12} lg={8}>
              {email ? (
                <EmailLink email={email} className="h6" />
              ) : (
                <Flex alignItems="center" className="mb-2">
                  <FontAwesomeIcon
                    icon={faAt}
                    transform="shrink-3 up-1"
                    className="me-2"
                  />
                  <UnknownBadge />
                </Flex>
              )}
              {phone ? (
                <PhoneCallLink
                  number={phone}
                  className="d-flex align-items-center gap-1 h6 mb-0 text-600"
                />
              ) : (
                <Flex alignItems="center">
                  <FontAwesomeIcon
                    icon={faPhone}
                    transform="shrink-3 up-1"
                    className="me-2"
                  />
                  <UnknownBadge />
                </Flex>
              )}
            </Col>

            <Col xs={12} lg={"auto"}>
              <div className="border-bottom border-lg-0 border-0 my-lg-0 my-3"></div>
              <Flex direction="column">
                <SoftBadge bg={"primary"}>
                  {isOwner ? "Gérant" : "Employé"}
                </SoftBadge>
                <h6 className="d-flex align-items-center gap-1 mb-0 mt-2">
                  <FontAwesomeIcon icon={faClock} transform="shrink-3 up-1" />
                  <span className="text-600">
                    {DateTime.fromISO(createdAt)
                      .setLocale("fr")
                      .toFormat("dd LLL yyyy")}
                  </span>
                </h6>
              </Flex>
            </Col>
          </Row>
        </div>
      ))}
    </PaginateCard>
  );
};

export default CompanyCoursePeopleCard;
